import * as etiProducts from '@eti/products';
import { travelerForm } from '@eti/travelers';
import { PRESELECTION_VALUES } from '../../../constants/productConstants';
import { getPersistedFormData } from './persistedData';

const {
  AIR_HELP,
  AIRPORT_TRANSFER,
  ALL_INCLUSIVE_PROTECTION,
  BAGGAGE_SERVICE,
  BANKRUPTCY_INSURANCE_COVER_GENIUS,
  CABIN_BAGGAGE,
  CANCEL_FOR_ANY_REASON,
  CANCELLATION_GUARANTEE,
  CANCELLATION_INSIDE_EU,
  CANCELLATION_INSURANCE_COVER_GENIUS,
  CANCELLATION_OUTSIDE_EU,
  CHECK_IN_BAGGAGE,
  CLIMATE_COMPENSATION,
  CO2_COMPENSATION,
  COMPREHENSIVE_INSURANCE_COVER_GENIUS,
  FAST_TRACK,
  FLEXIBLE_TICKET,
  MEAL,
  MOBILE_TRAVEL_PLAN,
  ONLINE_CHECK_IN,
  PLATINUM_SERVICE,
  ProductCardLayoutType,
  QPASS,
  SEAT_MAP,
  SIMPLE_VISA,
  SMS,
  TRAVEL_INSURANCE_INSIDE_EU,
  TRAVEL_INSURANCE_OUTSIDE_EU,
  TRAVEL_INSURANCE_COVER_GENIUS,
  TRIP_CANCELLATION_PROTECTION,
} = etiProducts;

export const AGE_PRODUCTS = [ONLINE_CHECK_IN, SMS, MOBILE_TRAVEL_PLAN];
export const PRODUCTS_PER_TRAVELER = [FLEXIBLE_TICKET];

const productNewStructure = [
  AIR_HELP,
  AIRPORT_TRANSFER,
  BAGGAGE_SERVICE,
  BANKRUPTCY_INSURANCE_COVER_GENIUS,
  CANCELLATION_GUARANTEE,
  CANCELLATION_INSIDE_EU,
  CANCELLATION_INSURANCE_COVER_GENIUS,
  CANCELLATION_OUTSIDE_EU,
  CO2_COMPENSATION,
  COMPREHENSIVE_INSURANCE_COVER_GENIUS,
  FAST_TRACK,
  FLEXIBLE_TICKET,
  MEAL,
  MOBILE_TRAVEL_PLAN,
  ONLINE_CHECK_IN,
  PLATINUM_SERVICE,
  SIMPLE_VISA,
  SMS,
  TRAVEL_INSURANCE_COVER_GENIUS,
  TRAVEL_INSURANCE_INSIDE_EU,
  TRAVEL_INSURANCE_OUTSIDE_EU,
];
const getSmallCardProp = (name) =>
  `TravelerDetails.Product.${name.charAt(0).toUpperCase()}${name.slice(1)}.SmallCardEnabled`;

export const getProductLayoutType = (id, p) => {
  const product = productNewStructure.find((item) => item.id === id);
  return product && p(getSmallCardProp(product.name))
    ? ProductCardLayoutType.Small
    : ProductCardLayoutType.Main;
};

export const flattenProducts = (products = []) => products.flat();

export const getClimateCompensationPriceWhenSelectedWithTrip = (products = []) => {
  const climateCompensationProduct = products.find(
    (product) => product.id === CLIMATE_COMPENSATION.id && product.selectedWithTrip,
  );
  if (climateCompensationProduct && climateCompensationProduct.selectedWithTrip) {
    return climateCompensationProduct?.sellSpecification?.price?.price?.value ?? null;
  }
  return null;
};

export const getFormSectionFromId = (id) => {
  const product = Object.values(etiProducts).find((etiProduct) => etiProduct.id === id);
  return product ? product.formSection : null;
};

export const generateNoneSelection = ({ priceRaw, isPreSelected }) => ({
  priceRaw: isPreSelected ? priceRaw : 0,
  value: isPreSelected,
});

export const generateProductPerTravelerSelection = ({
  name,
  sellSpecification = {},
  isPreSelected,
}) => ({
  [name]: {
    value: isPreSelected
      ? sellSpecification.sellPriceTravelers.map((traveler) => ({
          priceRaw: traveler.price.price.value,
          value: traveler.travelerId,
        }))
      : [{ value: 'NONE', priceRaw: 0 }],
  },
});

export const generateCancellationSelection = ({ name, sellSpecification }) => ({
  [name]: {
    value: {
      value: true,
      priceRaw: sellSpecification.sellPriceTravelers.reduce(
        (total, traveler) => total + traveler.price.price.value,
        0,
      ),
    },
  },
});

export const generateSingleChoiceSelection = ({ name, sellSpecification }) => ({
  [name]: {
    value: {
      value: true,
      priceRaw: sellSpecification.price.price.value,
    },
  },
});

export const generateAirportTransferSelection = ({ name, sellSpecification }) => ({
  [name]: {
    value: {
      value: true,
      priceRaw: sellSpecification.externalChoices?.filter(
        (choice) => choice.ancillaryOption === 'CHEAPEST',
      )?.[0]?.pricePerTravelers?.[0]?.price?.price.value,
    },
  },
});

export const addProductToTrip = ({ id, sellSpecification }) => {
  const productSelections = {
    [FLEXIBLE_TICKET.id]: () =>
      generateProductPerTravelerSelection({
        name: FLEXIBLE_TICKET.name,
        sellSpecification,
        isPreSelected: true,
      }),
    [CANCELLATION_INSIDE_EU.id]: () =>
      generateCancellationSelection({ name: CANCELLATION_INSIDE_EU.name, sellSpecification }),
    [CANCELLATION_OUTSIDE_EU.id]: () =>
      generateCancellationSelection({ name: CANCELLATION_OUTSIDE_EU.name, sellSpecification }),
    [CLIMATE_COMPENSATION.id]: () => {
      const priceRaw = sellSpecification?.price?.price?.value;
      return {
        [getFormSectionFromId(id)]: {
          value: generateNoneSelection({ priceRaw, isPreSelected: true }),
        },
      };
    },
    [CANCELLATION_GUARANTEE.id]: () =>
      generateSingleChoiceSelection({
        name: CANCELLATION_GUARANTEE.name,
        sellSpecification,
      }),
    [CANCEL_FOR_ANY_REASON.id]: () =>
      generateSingleChoiceSelection({ name: CANCEL_FOR_ANY_REASON.name, sellSpecification }),
    [QPASS.id]: () => generateSingleChoiceSelection({ name: QPASS.name, sellSpecification }),
    [MOBILE_TRAVEL_PLAN.id]: () =>
      generateSingleChoiceSelection({ name: MOBILE_TRAVEL_PLAN.name, sellSpecification }),
    [AIRPORT_TRANSFER.id]: () =>
      generateAirportTransferSelection({ name: AIRPORT_TRANSFER.name, sellSpecification }),
  };

  return productSelections[id] ? productSelections[id]() : {};
};

export const filterProductCards = (productList) =>
  productList.map((products) =>
    products.filter(
      (product) =>
        !(
          Number(product.id) === MOBILE_TRAVEL_PLAN.id &&
          product?.sellSpecification.price.price.value === 0
        ),
    ),
  );

export const shouldPreSelect = (product) => {
  const productsFreeOfCharge = [QPASS.id, MOBILE_TRAVEL_PLAN.id];
  const isFreeAndPriceZero =
    productsFreeOfCharge.includes(Number(product.id)) &&
    product?.sellSpecification.price.price.value === 0;

  return product.selectedWithTrip || isFreeAndPriceZero;
};

export const addExtraProductsSelectedOnTrip = (products = []) =>
  flattenProducts(products).reduce(
    (acc, product) => (shouldPreSelect(product) ? { ...acc, ...addProductToTrip(product) } : acc),
    {},
  );

const isPreSelectionYes = (preSelection) => preSelection === PRESELECTION_VALUES.yes;

const generateFormBaggageTravelerValue = ({
  preselected,
  travelerId,
  priceRaw,
  markup,
  numberOfPieces,
  weight,
  weightUnit,
  hasMultipleWeightOptions,
}) => {
  let value;

  if (hasMultipleWeightOptions) {
    value = preselected ? `${preselected}-${weight}${weightUnit}` : 'false';
  } else {
    value = String(preselected);
  }

  return {
    value,
    id: travelerId,
    markup,
    priceRaw,
    numberOfPieces,
    weight,
    weightUnit,
  };
};

export const generateBaggageTravelerSelection = ({
  travelers,
  sellPriceBaggage,
  isShownPerTraveler,
}) => {
  let preselectedValues = [];

  const baggageTravelersValues = travelers
    .filter((traveler) => traveler.ageType !== 'INF')
    .reduce((acc, { id }) => {
      const travelerBaggageSpecification = sellPriceBaggage.find(
        ({ travelerId }) => travelerId === id,
      );

      const baggageOption = travelerBaggageSpecification?.options.find(
        (option) => option?.preselected,
      );

      const {
        preselected = false,
        price = null,
        numberOfUnits = 0,
        weight = 0,
        weightUnit = '',
      } = baggageOption || {};

      const priceRaw = price?.price?.value ?? 0;
      const markup = price?.markup?.value ?? 0;
      const field = isShownPerTraveler ? `checkInBaggage-traveler-${id}` : `traveler-${id}`;
      const hasMultipleWeightOptions = travelerBaggageSpecification?.hasMultipleWeightOptions;

      acc[field] = generateFormBaggageTravelerValue({
        preselected,
        travelerId: id,
        priceRaw,
        markup,
        numberOfPieces: numberOfUnits,
        weight,
        weightUnit,
        hasMultipleWeightOptions,
      });

      preselectedValues = [...preselectedValues, preselected];

      return acc;
    }, {});

  const isAnyBaggagePreselected = preselectedValues.some((value) => value === true);
  baggageTravelersValues['checkInBaggage-toggleTravelers'] = isAnyBaggagePreselected;

  return baggageTravelersValues;
};

const generateCabinBaggageSelection = ({ isPreSelected, travelers, sellPriceTravelers }) => {
  return travelers
    .filter((traveler) => traveler.ageType !== 'INF')
    .reduce((acc, { id }) => {
      const traveler = sellPriceTravelers.find(({ travelerId }) => travelerId === id);
      const priceRaw = traveler?.price?.price?.value ?? 0;
      const markup = traveler?.price?.markup?.value ?? 0;
      const field = `cabinBaggage-traveler-${id}`;

      acc[field] = generateFormBaggageTravelerValue({
        preselected: isPreSelected,
        travelerId: id,
        priceRaw,
        markup,
      });

      return acc;
    }, {});
};

const generateTravelerPriceSum = ({ sellPriceTravelers }) =>
  sellPriceTravelers.reduce((acc, priceTraveler) => acc + priceTraveler.price.price.value, 0);

export const generateTravelerSelection = (formSection, sellSpecification, productPreSelection) => ({
  [formSection]: {
    value: generateNoneSelection({
      priceRaw: generateTravelerPriceSum(sellSpecification),
      isPreSelected: isPreSelectionYes(productPreSelection),
    }),
  },
});

const generateProductFormValue = (product, travelers, formSection, isShownPerTraveler) => {
  const sellSpecification = product?.component?.props?.sellSpecification ?? null;
  const { productPreSelection, __typename } = product;

  if (!sellSpecification) {
    return {};
  }

  const isPreSelected = isPreSelectionYes(productPreSelection);

  switch (__typename) {
    case 'SellSpecificationBaggageNew':
      return {
        [formSection]: generateBaggageTravelerSelection({
          travelers,
          sellPriceBaggage: sellSpecification?.sellPriceBaggage,
          isShownPerTraveler,
        }),
      };
    case 'SellSpecificationTravelerNew': {
      const isProductPerTraveler = PRODUCTS_PER_TRAVELER.find(({ id }) => product.id === id);
      return isProductPerTraveler
        ? generateProductPerTravelerSelection({
            name: formSection,
            sellSpecification,
            isPreSelected,
          })
        : generateTravelerSelection(formSection, product.sellSpecification, productPreSelection);
    }
    case 'SellSpecificationNoneNew':
      return {
        [formSection]: {
          value: generateNoneSelection({
            priceRaw: sellSpecification?.price?.price?.value ?? 0,
            isPreSelected,
          }),
        },
      };
    case 'SellCabinBaggageNew':
      return {
        [formSection]: generateCabinBaggageSelection({
          isPreSelected,
          travelers,
          sellPriceTravelers: sellSpecification?.sellPriceTravelers,
        }),
      };
    default:
      return {};
  }
};

export const generateProductInitialValues = (
  product,
  travelers,
  shouldShowProductsPerTravelerNew,
  productsToDisplayPerTraveler,
) => {
  let isShownPerTraveler = false;
  if (shouldShowProductsPerTravelerNew && product.code === 'BAGGAGE') {
    const productsToDisplayPerTravelerArray = productsToDisplayPerTraveler.split(',');

    isShownPerTraveler = productsToDisplayPerTravelerArray.includes(product.code);
  }

  const formSection = getFormSectionFromId(product.id);
  return !formSection
    ? {}
    : generateProductFormValue(product, travelers, formSection, isShownPerTraveler);
};

const getInitialTravelersValue = (travelers = []) =>
  travelers.reduce((acc, { id, showDateOfBirth }) => {
    const dateOfBirth = getPersistedFormData(`${travelerForm.DATE_OF_BIRTH}-${id}`);
    return {
      ...acc,
      [`traveler-${id}`]: {
        ...(showDateOfBirth && dateOfBirth ? { dateOfBirth } : {}),
      },
    };
  }, {});

export const generateManulifeInitialValues = (products, travelers) =>
  products.reduce(
    (acc, { formSection }) => ({
      ...acc,
      [formSection]: { travelers: getInitialTravelersValue(travelers) },
    }),
    {},
  );
export const generateSimpleVisaInitialValues = (product, travelers) => {
  const preselectedChoices = SIMPLE_VISA.simpleVisaTransformer.getPreselectedTravelerChoices(
    travelers,
    product.sellSpecification.availableChoices,
    product.sellSpecification.preselectedChoice,
  );

  return Object.values(preselectedChoices).length
    ? {
        [SIMPLE_VISA.name]: {
          travelers: preselectedChoices,
        },
      }
    : {};
};

const manulifeProducts = {
  [ALL_INCLUSIVE_PROTECTION.id]: ALL_INCLUSIVE_PROTECTION,
  [TRIP_CANCELLATION_PROTECTION.id]: TRIP_CANCELLATION_PROTECTION,
};

// TODO: refactor and untangle these by composing the initial values for different products instead: https://jira.etraveli.net/browse/AN-2481
export const generateInitialValuesExtraProducts = (
  products = [],
  travelers = [],
  shouldShowProductsPerTravelerNew = false,
  productsToDisplayPerTraveler = '',
) => {
  let initialValues = {};
  const flattenedProducts = flattenProducts(products);

  const availableManulifeProducts = flattenedProducts.reduce(
    (acc, { id }) => [...acc, ...(manulifeProducts[id] ? [manulifeProducts[id]] : [])],
    [],
  );
  if (availableManulifeProducts) {
    initialValues = {
      ...initialValues,
      ...generateManulifeInitialValues(availableManulifeProducts, travelers),
    };
  }

  const simpleVisaProduct = flattenedProducts.find((product) => SIMPLE_VISA.id === product.id);
  if (simpleVisaProduct) {
    initialValues = {
      ...initialValues,
      ...generateSimpleVisaInitialValues(simpleVisaProduct, travelers),
    };
  }

  return flattenedProducts.reduce((acc, product) => {
    const preSelection = product?.productPreSelection;
    if (preSelection !== PRESELECTION_VALUES.none) {
      return {
        ...acc,
        ...generateProductInitialValues(
          product,
          travelers,
          shouldShowProductsPerTravelerNew,
          productsToDisplayPerTraveler,
        ),
      };
    }
    return { ...acc };
  }, initialValues);
};

export const getProductIndex = (id) => {
  const orderedProducts = [
    CABIN_BAGGAGE.id,
    CHECK_IN_BAGGAGE.id,
    QPASS.id,
    FLEXIBLE_TICKET.id,
    CANCELLATION_GUARANTEE.id,
    CANCELLATION_INSIDE_EU.id,
    CANCELLATION_OUTSIDE_EU.id,
    SIMPLE_VISA.id,
    ONLINE_CHECK_IN.id,
    AIR_HELP.id,
    MOBILE_TRAVEL_PLAN.id,
    SMS.id,
    MEAL.id,
    TRAVEL_INSURANCE_INSIDE_EU.id,
    TRAVEL_INSURANCE_OUTSIDE_EU.id,
    BAGGAGE_SERVICE.id,
    SEAT_MAP.id,
    CANCEL_FOR_ANY_REASON.id,
    PLATINUM_SERVICE.id,
  ];

  const index = orderedProducts.indexOf(id) + 1;
  return index > 0 ? index : 100;
};

export const isCheckInBaggageNotAvailable = ({
  includedCheckedBaggage,
  products = [],
  travelerFormProducts,
} = {}) => {
  if (!includedCheckedBaggage && !products) {
    return false;
  }

  const availableCheckedBaggage = products.some(({ id }) => id === CHECK_IN_BAGGAGE.id);

  const availableCheckedBaggagePerTraveler = travelerFormProducts?.some(
    ({ id }) => id === CHECK_IN_BAGGAGE.id,
  );
  return !includedCheckedBaggage && !availableCheckedBaggage && !availableCheckedBaggagePerTraveler;
};

export const isAncillaryBundleProduct = ({
  ancillaryBundleSelectedValue,
  id: productId,
  isAncillaryBundleEmptyOptionSelected,
  isAncillaryBundlingEnabled,
  isOnSecondaryProductsSection,
  mutuallyExclusive,
  products,
  purchasableMuxProducts,
} = {}) => {
  if (
    !products ||
    !isAncillaryBundlingEnabled ||
    isAncillaryBundleEmptyOptionSelected ||
    (isOnSecondaryProductsSection && !ancillaryBundleSelectedValue)
  ) {
    return false;
  }

  const availableAncillaryBundle = products.find((product) => product.id === 'ancillary-bundle');

  if (!availableAncillaryBundle) {
    return false;
  }

  if (productId === mutuallyExclusive.id) {
    const purchasableIds = purchasableMuxProducts?.map((p) => String(p.id)) || [];
    return availableAncillaryBundle.ids?.some((id) => purchasableIds.includes(String(id))) || false;
  }

  return availableAncillaryBundle.ids?.includes(productId) || false;
};
